import type { EntityViewLevel, LayerModel, OperationalAttributeField } from '../core/types';
import {
  createMasterAttributeJoinTarget,
  DEFAULT_ATTRIBUTE_JOIN_NORMALIZATION,
  normalizeAttributeJoinValue,
  suggestAttributeJoins,
  type AttributeJoinKeyExpression,
  type AttributeJoinSuggestion,
  type AttributeJoinTarget,
  type ExcelTable,
} from './excelImport';

/** A ranked relation proposal measured against the spreadsheet rows (what the user reasons about). */
export interface ExcelRelationSuggestion extends AttributeJoinSuggestion {
  readonly level: EntityViewLevel;
  /** Spreadsheet rows whose key finds at least one entity. */
  readonly matchedRows: number;
  readonly totalRows: number;
  /** Average number of entities reached by one matched row (1 = one row per entity). */
  readonly entitiesPerRow: number;
  /** Average number of spreadsheet rows sharing one matched key (1 = no repeated keys). */
  readonly rowsPerKey: number;
}

export interface ExcelRelationAnalysis {
  /** Level the spreadsheet appears to describe ("Parece información por Finca"). */
  readonly level: EntityViewLevel;
  readonly target: AttributeJoinTarget;
  readonly suggestions: readonly ExcelRelationSuggestion[];
  /** Columns acting as identifiers (keys or alternative keys); not proposed as fields to add. */
  readonly identityColumns: readonly string[];
  readonly confident: boolean;
}

const expressionKey = (expression: AttributeJoinKeyExpression, values: Readonly<Record<string, unknown>>): string => {
  const parts = expression.components.map((component) => values[component]);
  if (parts.some((part) => part === null || part === undefined || part === '')) return '';
  return normalizeAttributeJoinValue(parts.map(String).join(expression.separator), DEFAULT_ATTRIBUTE_JOIN_NORMALIZATION);
};

function measure(target: AttributeJoinTarget, table: ExcelTable, suggestion: AttributeJoinSuggestion): ExcelRelationSuggestion {
  const mapping = suggestion.mappings[0]!;
  const entitiesByKey = new Map<string, number>();
  target.entities.forEach((entity) => { const key = expressionKey(mapping.target, entity.valuesByFieldId); if (key) entitiesByKey.set(key, (entitiesByKey.get(key) ?? 0) + 1); });
  let matchedRows = 0; let reached = 0; const matchedKeys = new Set<string>();
  table.rows.forEach((row) => { const key = expressionKey(mapping.source, row); const count = entitiesByKey.get(key) ?? 0; if (count) { matchedRows += 1; reached += count; matchedKeys.add(key); } });
  return { ...suggestion, level: target.entityLevel, matchedRows, totalRows: table.rows.length, entitiesPerRow: matchedRows ? reached / matchedRows : 0, rowsPerKey: matchedKeys.size ? matchedRows / matchedKeys.size : 0 };
}

const relationScore = (suggestion: ExcelRelationSuggestion, preferred: EntityViewLevel): number => {
  const rowCoverage = suggestion.totalRows ? suggestion.matchedRows / suggestion.totalRows : 0;
  const oneToOne = suggestion.entitiesPerRow > 0 && suggestion.entitiesPerRow <= 1.05 && suggestion.rowsPerKey <= 1.05 ? 1 : 0;
  return rowCoverage * 2 + oneToOne + (suggestion.confidence === 'name' ? 0.3 : 0) + (suggestion.level === preferred ? 0.1 : 0) - (suggestion.keyCollisions ? 3 : 0);
};

/**
 * Infers which entity level a spreadsheet describes and the safest relation, derived only from content:
 * row coverage, cardinality (one row ↔ one entity) and column semantics. Nothing is hard-coded to a file;
 * the generic join engine remains the only execution path.
 */
export function analyzeExcelRelation(model: LayerModel, table: ExcelTable, fields: readonly OperationalAttributeField[], preferred: EntityViewLevel, levels: readonly EntityViewLevel[] = ['finca', 'suerte', 'tablon']): ExcelRelationAnalysis {
  const candidates = levels.map((level) => {
    const target = createMasterAttributeJoinTarget(model, model.id, level, fields);
    const suggestions = suggestAttributeJoins(target, table).slice(0, 5).map((suggestion) => measure(target, table, suggestion))
      .sort((a, b) => relationScore(b, preferred) - relationScore(a, preferred));
    return { level, target, suggestions, score: suggestions[0] ? relationScore(suggestions[0], preferred) : -Infinity };
  });
  const best = candidates.reduce((winner, candidate) => candidate.score > winner.score ? candidate : winner, candidates.find(({ level }) => level === preferred) ?? candidates[0]!);
  const top = best.suggestions[0];
  const identityColumns = [...new Set(best.suggestions.filter((suggestion) => top && suggestion.matchedRows >= top.matchedRows * 0.9).flatMap(({ mappings }) => mappings.flatMap(({ source }) => source.components)))];
  const confident = Boolean(top && top.totalRows && top.matchedRows / top.totalRows >= 0.8 && top.entitiesPerRow <= 1.05 && top.rowsPerKey <= 1.05 && !top.keyCollisions);
  return { level: best.level, target: best.target, suggestions: best.suggestions, identityColumns, confident };
}
